import axios from "axios";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import ColorSchemeToggle from "../components/common/ColorSchemeToggle";
import { useAuth } from "../contexts/AuthContext";

interface LoginFormValues {
  username: string;
  password: string;
}

/**
 * Where to go after signing in: the `returnTo` query value when it is a path on this site, so a link cannot use the
 * login page to send someone to another site.
 */
const afterLoginPath = (returnTo: string | null): string =>
  returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//") && !returnTo.startsWith("/\\")
    ? returnTo
    : "/organizations";

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormValues>({
    defaultValues: { username: "", password: "" },
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const response = await axios.post("/api/signin", {
        username: data.username,
        password: data.password,
      });

      const { accessToken, refreshToken, mustChangePassword } = response.data;
      login(accessToken, refreshToken);
      navigate(mustChangePassword ? "/change-password" : afterLoginPath(searchParams.get("returnTo")));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        if (status && status >= 500) {
          form.setError("root", { message: "Service is temporarily unavailable. Please try again." });
        } else if (status === 401 || status === 403) {
          form.setError("root", { message: "Invalid username or password" });
        } else if (status === 429) {
          form.setError("root", { message: "Too many login attempts. Please try again in a moment." });
        } else {
          form.setError("root", { message: "An unexpected error occurred. Please try again." });
        }
      } else {
        form.setError("root", { message: "An unexpected error occurred. Please try again." });
      }
    }
  };

  return (
    <div className="relative flex flex-1 items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 dark:to-primary/10 p-4">
      <div className="absolute top-4 right-4">
        <ColorSchemeToggle />
      </div>
      <div className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-xl font-bold tracking-tight">Neos Files Sync</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your account</p>
        </div>
        <Card className="w-full">
          <CardContent className="pt-6 space-y-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="username"
                rules={{ required: "Username is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        autoComplete="username"
                        disabled={form.formState.isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                rules={{ required: "Password is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          disabled={form.formState.isSubmitting}
                          className="pr-9"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
                          tabIndex={-1}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full mt-2"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                Log in
              </Button>
              {form.formState.errors.root && (
                <Alert variant="destructive">
                  <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
                </Alert>
              )}
            </form>
          </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
